
import { UserRole } from '../types';
import { debug } from './auth_utils';
import * as authSession from './auth_session';
import { db } from './db'; 
import { isPlaceholderEmail } from '../api/_lib/placeholderEmail';
import { MAX_SELLERS_PER_REQUEST, type SellerCredentials } from '../api/_lib/sellerList';
import { apiFetch } from './apiFetch';

// Calls the api/auth/create-user.ts route
const invokeCreateUser = async (payload: any) => {
    let response: Response;
    try {
        response = await apiFetch('/api/auth/create-user', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
    } catch (e) {
        throw new Error("Kunde inte nå servern. Kontrollera din anslutning.");
    }

    const data = await response.json().catch(() => null);

    if (!response.ok) {
        console.error("Create User API Error:", data);
        throw new Error(data?.error || "Registreringen misslyckades på servern.");
    }

    if (data && data.error) {
        throw new Error(data.error);
    }

    return data;
};

export async function signUp(email: string, password: string, name: string, role: UserRole, classId?: string, requestedGroup?: string) {
    debug('SIGNUP', `Startar registrering via /api/auth/create-user: ${email}`);

    // 1. Create User via API route
    await invokeCreateUser({
        email,
        password,
        name,
        role,
        classId: classId || null,
        // Frivilligt svar på "Vilken grupp tillhör du?", som admin matchar vid godkännandet.
        requestedGroup: requestedGroup || null
    });

    // 2. Trigger Welcome Email (Reliable API for signups)
    if (email && !isPlaceholderEmail(email)) {
        await db.sendWelcomeEmail({ to: email, name });
    }

    // 3. Perform Server-Side Login via API
    try {
        const user = await authSession.signIn(email, password);
        return user;
    } catch (error: any) {
        throw error;
    }
}

export async function createUserManaged(email: string, name: string, role: UserRole, classId: string, uniqueSlug: string, isPlaceholderEmail: boolean = false, sendWelcomeEmail: boolean = false) {
      debug('CREATE_MANAGED', `Skapar användare: ${name}`);
      
      const tempPassword = Math.random().toString(36).slice(-8) + "123!";
      
      // Pass the welcome email flag to the API route.
      // It will handle generating the Magic Link and sending it.
      const response = await invokeCreateUser({
          email,
          password: tempPassword,
          name,
          role,
          classId: classId || null,
          uniqueSlug,
          isManaged: isPlaceholderEmail,
          sendWelcomeEmail: sendWelcomeEmail && !isPlaceholderEmail,
          originUrl: window.location.origin,
          requireCaller: true
      });

      return { user: response.user, tempPassword, welcomeEmailSent: response.welcomeEmailSent === true };
}

export interface SellerListResult {
    created: SellerCredentials[];
    failed: { name: string; error: string }[];
}

/**
 * Skapar säljare utan e-post från en lista, några namn per anrop så att
 * inget anrop blir för långt. Ett anrop som misslyckas markerar bara sina
 * namn som misslyckade; resten fortsätter.
 */
export async function createSellersFromList(
    classId: string,
    names: string[],
    onProgress?: (done: number, total: number) => void,
): Promise<SellerListResult> {
    const result: SellerListResult = { created: [], failed: [] };
    for (let i = 0; i < names.length; i += MAX_SELLERS_PER_REQUEST) {
        const chunk = names.slice(i, i + MAX_SELLERS_PER_REQUEST);
        try {
            const data = await invokeCreateUser({ action: 'create-sellers', classId, names: chunk });
            result.created.push(...(data.created || []));
            result.failed.push(...(data.failed || []));
        } catch (e: any) {
            chunk.forEach(name => result.failed.push({ name, error: e?.message || 'Kunde inte skapa kontot.' }));
        }
        onProgress?.(Math.min(i + chunk.length, names.length), names.length);
    }
    return result;
}

/** Nytt lösenord till en säljare utan e-post, för en ny inloggningslapp. */
export async function newSellerSlip(userId: string): Promise<SellerCredentials> {
    const data = await invokeCreateUser({ action: 'reset-seller-password', userId });
    return data.seller as SellerCredentials;
}
